import React, { useEffect, useState } from "react";
import axios from "axios";
import { ExternalLink, ShoppingBag, ShoppingCart, Loader2 } from "lucide-react";
import { fetchProducts, createCartCheckoutUrl, SHOPIFY_CONFIGURED, SHOPIFY_DOMAIN } from "../lib/shopify";
import PayPalTipJar from "./PayPalTipJar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ACCENT = "#D4AF37";

export default function MerchStore({ section, isNight, onUpdated }) {
    const domain = (section?.shopify_domain || SHOPIFY_DOMAIN || "").trim();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [addingId, setAddingId] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                if (!SHOPIFY_CONFIGURED) throw new Error("Storefront token not configured");
                const items = await fetchProducts(24);
                if (!cancelled) setProducts(items);
            } catch (e) {
                console.error("Shopify fetch failed", e);
                if (!cancelled) setError(e.message || "Unable to load products");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Auto-mark has_content once real products land.
    useEffect(() => {
        if (products.length > 0 && !section?.has_content) {
            axios.patch(`${API}/sections/${section.id}`, { has_content: true })
                .then((res) => onUpdated && onUpdated(res.data))
                .catch(() => { });
        }
    }, [products.length, section?.has_content, section?.id, onUpdated]);

    const onAddToCart = async (p) => {
        if (!p?.variantId) return;
        setAddingId(p.id);
        try {
            const url = await createCartCheckoutUrl(p.variantId, 1);
            window.open(url, "_blank", "noopener,noreferrer");
        } catch (e) {
            console.error("Add-to-cart failed", e);
            alert("Could not start checkout. Please try again.");
        } finally {
            setAddingId(null);
        }
    };

    const storeUrl = domain
        ? (/^https?:\/\//.test(domain) ? domain : `https://${domain}`)
        : "";

    return (
        <div
            data-testid="merch-store"
            className="absolute inset-0 z-30 overflow-y-auto"
            style={{
                background: isNight
                    ? "radial-gradient(1200px 700px at 30% 20%, rgba(30,30,44,0.9), rgba(6,6,10,0.98) 60%)"
                    : "radial-gradient(1200px 700px at 30% 20%, rgba(255,244,220,0.85), rgba(20,20,26,0.96) 65%)",
            }}
        >
            <div className="max-w-6xl mx-auto px-6 md:px-10 py-24">
                <div className="text-[10px] tracking-[0.5em] uppercase mb-4" style={{ color: ACCENT }}>
                    Caprio · Merch Collection
                </div>
                <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div>
                        <h1
                            className="font-serif-display text-5xl md:text-7xl uppercase tracking-luxe leading-none"
                            style={{ color: "#fff", textShadow: "0 4px 30px rgba(0,0,0,0.7)" }}
                        >
                            {section?.custom_label || section?.label || "Merch Store"}
                        </h1>
                        <p className="mt-4 text-sm max-w-lg" style={{ color: "rgba(255,255,255,0.75)" }}>
                            Official Caprio Chateau merch — live from Shopify. Add to cart, check out securely, or tip the studio below.
                        </p>
                    </div>
                    {storeUrl && (
                        <a
                            href={storeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="merch-visit-shopify"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[11px] tracking-[0.35em] uppercase"
                            style={{ background: "transparent", color: ACCENT, border: `1px solid ${ACCENT}` }}
                        >
                            Visit Shopify <ExternalLink size={13} />
                        </a>
                    )}
                </div>

                {loading && (
                    <div
                        className="mt-12 grid place-items-center py-16"
                        data-testid="merch-loading"
                        style={{ color: ACCENT }}
                    >
                        <Loader2 size={28} className="animate-spin" />
                        <p className="mt-3 text-[10px] tracking-[0.4em] uppercase">Loading collection…</p>
                    </div>
                )}

                {!loading && error && (
                    <div
                        className="mt-12 mx-auto max-w-xl rounded-2xl px-6 py-8 text-center"
                        data-testid="merch-error"
                        style={{
                            background: "rgba(255,255,255,0.04)",
                            border: `1px solid ${ACCENT}55`,
                            color: "rgba(255,255,255,0.8)",
                        }}
                    >
                        <ShoppingBag size={36} className="mx-auto mb-3" style={{ color: ACCENT }} />
                        <div className="text-sm">
                            Products couldn't load right now. {storeUrl && (
                                <> Head over to <a href={storeUrl} target="_blank" rel="noopener noreferrer" style={{ color: ACCENT, textDecoration: "underline" }}>the Shopify store</a> to browse.</>
                            )}
                        </div>
                    </div>
                )}

                {!loading && !error && products.length === 0 && (
                    <div
                        className="mt-12 mx-auto max-w-xl rounded-2xl px-6 py-10 text-center"
                        data-testid="merch-empty"
                        style={{
                            background: "rgba(255,255,255,0.04)",
                            border: `1px solid ${ACCENT}55`,
                        }}
                    >
                        <ShoppingBag size={44} className="mx-auto mb-4" style={{ color: ACCENT }} />
                        <div className="font-serif-display text-3xl uppercase tracking-luxe" style={{ color: "#fff" }}>
                            Coming Soon
                        </div>
                        <p className="mt-3 text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
                            The Shopify collection is being curated. Check back shortly.
                        </p>
                    </div>
                )}

                {!loading && products.length > 0 && (
                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="merch-grid">
                        {products.map((p) => (
                            <div
                                key={p.id}
                                data-testid={`merch-product-${p.handle}`}
                                className="group rounded-2xl overflow-hidden transition-transform hover:-translate-y-1 flex flex-col"
                                style={{
                                    background: "rgba(255,255,255,0.04)",
                                    border: `1px solid ${ACCENT}55`,
                                }}
                            >
                                <a
                                    href={p.onlineStoreUrl || storeUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="aspect-square overflow-hidden bg-black block"
                                    data-testid={`merch-product-image-${p.handle}`}
                                >
                                    {p.image ? (
                                        <img
                                            src={p.image}
                                            alt={p.imageAlt}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className="w-full h-full grid place-items-center opacity-40" style={{ color: ACCENT }}>
                                            <ShoppingBag size={48} />
                                        </div>
                                    )}
                                </a>
                                <div className="p-4 flex-1 flex flex-col">
                                    <div className="text-sm font-medium" style={{ color: "#fff" }}>{p.title}</div>
                                    <div className="mt-1 text-xs tracking-[0.2em] uppercase" style={{ color: ACCENT }}>
                                        {p.price || "—"}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => onAddToCart(p)}
                                        disabled={!p.variantId || !p.available || addingId === p.id}
                                        data-testid={`merch-add-to-cart-${p.handle}`}
                                        className="mt-4 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-[11px] tracking-[0.3em] uppercase transition-all hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed"
                                        style={{ background: ACCENT, color: "#0a0a0a" }}
                                    >
                                        {addingId === p.id ? (
                                            <><Loader2 size={13} className="animate-spin" /> Opening…</>
                                        ) : p.available ? (
                                            <><ShoppingCart size={13} /> Add to Cart</>
                                        ) : (
                                            <>Sold Out</>
                                        )}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* PayPal Tip Jar */}
                <div className="mt-16" data-testid="merch-tip-jar-wrap">
                    <PayPalTipJar isNight={isNight} recipientLabel={section?.custom_label || "Caprio"} />
                </div>
            </div>
        </div>
    );
}
